import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Globe, Send, CheckCircle2 } from "lucide-react";
import { company } from "../mock";

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sent, setSent] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Care Inquiry from ${form.name || "Website"}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nService Needed: ${form.service}\n\nMessage:\n${form.message}`
    );
    window.location.href = `mailto:${company.email}?subject=${subject}&body=${body}`;
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  const info = [
    { icon: Phone, label: "Call Us", value: company.phone, href: `tel:${company.phoneLink}` },
    { icon: Mail, label: "Email Us", value: company.email, href: `mailto:${company.email}` },
    { icon: Globe, label: "Website", value: company.website, href: company.websiteLink },
    { icon: MapPin, label: "Visit Us", value: company.address, href: "#" },
    { icon: Clock, label: "Business Hours", value: company.hours, href: "#" },
  ];

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-800 outline-none focus:border-[#14357e] focus:bg-white focus:ring-2 focus:ring-[#14357e]/15 transition";

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center reveal">
          <span className="eyebrow text-[#e07c24]">Contact Us</span>
          <h2 className="font-display font-bold text-[#0e2a63] text-[34px] sm:text-[46px] mt-4">
            Let&rsquo;s Talk About Your Care Needs
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto mt-4 text-[17px]">
            Reach out for a free consultation. We&rsquo;ll help you build a care plan
            that&rsquo;s right for your loved ones.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 mt-14">
          {/* Info card */}
          <div className="reveal rounded-3xl p-9 text-white relative overflow-hidden" style={{ background: "linear-gradient(160deg, #0e2a63, #14357e)" }}>
            <div className="absolute -right-10 -top-10 w-52 h-52 rounded-full" style={{ background: "rgba(224,124,36,0.18)" }} />
            <h3 className="font-display font-bold text-2xl relative">Get in Touch</h3>
            <p className="text-white/70 mt-2 relative text-[15px]">
              We&rsquo;re here for you and your family, 6 days a week.
            </p>
            <div className="mt-8 flex flex-col gap-6 relative">
              {info.map((it) => (
                <a key={it.label} href={it.href} className="flex items-start gap-4 group">
                  <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-[#f2a25a] group-hover:bg-[#e07c24] group-hover:text-white transition-colors shrink-0">
                    <it.icon size={22} />
                  </span>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider">{it.label}</p>
                    <p className="font-semibold mt-0.5">{it.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="reveal reveal-delay-1 rounded-3xl p-9 shadow-xl border border-slate-100">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <CheckCircle2 size={64} className="text-[#3aa84f]" />
                <h3 className="font-display font-bold text-2xl text-[#0e2a63] mt-4">Thank You!</h3>
                <p className="text-slate-600 mt-2 max-w-sm">
                  Your email is ready to send. If it didn&rsquo;t open, call us anytime at
                  <a href={`tel:${company.phoneLink}`} className="text-[#e07c24] font-semibold"> {company.phone}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required name="name" value={form.name} onChange={change} placeholder="Full Name" className={inputCls} />
                  <input required name="phone" value={form.phone} onChange={change} placeholder="Phone Number" className={inputCls} />
                </div>
                <input type="email" name="email" value={form.email} onChange={change} placeholder="Email Address" className={inputCls} />
                <select name="service" value={form.service} onChange={change} className={inputCls}>
                  <option value="">Service Needed</option>
                  <option>Companionship</option>
                  <option>Home Health Aide / CNA</option>
                  <option>Nursing Services</option>
                  <option>Postnatal Services</option>
                  <option>Terminal Illness Care</option>
                  <option>Live In Services</option>
                  <option>Homemaker / Nanny</option>
                  <option>Personal Care</option>
                  <option>Home Assistant & Hospice Care</option>
                </select>
                <textarea name="message" value={form.message} onChange={change} rows={4} placeholder="How can we help you or your loved one?" className={inputCls} />
                <button type="submit" className="btn-orange inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-[15px]">
                  <Send size={18} /> Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
